import SCHEMA_CONSTANTS from 'vets-json-schema/dist/constants.json';
import { replaceStrValues } from 'platform/shared/health-apps';
import {
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderItemSubsequentPageTitleUI,
  arrayBuilderYesNoUI,
  arrayBuilderYesNoSchema,
  currentOrPastDateUI,
  currentOrPastDateSchema,
  textUI,
  textSchema,
} from 'platform/forms-system/src/js/web-component-patterns';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import VaMedicalCenter from '../../../components/FormFields/VaMedicalCenter';
import { transformDate } from '../../../utils/helpers';
import { validations } from '../../validations';
import content from '../../../utils/content.json';

const STATES_USA = SCHEMA_CONSTANTS.formProfileStates;

const isItemIncomplete = (item, fullData) => {
  const facilitySelector = fullData?.survivorsBenefitsFacilitySelector;
  return facilitySelector
    ? !item?.facilityInfo?.vaMedicalCenterName ||
        !item?.facilityInfo?.state ||
        !item?.facilityInfo?.city
    : !item?.vaMedicalCenterName || !item?.state || !item?.city;
};

/** @type {ArrayBuilderOptions} */
export const options = {
  arrayPath: 'treatments',
  nounSingular: content['claim-information-treatment-noun-singular'],
  nounPlural: content['claim-information-treatment-noun-plural'],
  required: false,
  maxItems: 3,
  isItemIncomplete,
  text: {
    summaryTitle: content['claim-information-treatment-summary-title'],
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      `${content['claim-information-treatment-noun-singular']}?`,
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      `${content['claim-information-treatment-noun-singular']}?`,
    ),
    cancelAddDescription:
      content['claim-information-treatment-cancel-add-description'],
    cancelAddReviewDescription:
      content['claim-information-treatment-cancel-add-description'],
    cancelEditDescription:
      content['claim-information-treatment-cancel-edit-description'],
    cancelEditReviewDescription:
      content['claim-information-treatment-cancel-edit-description'],
    cancelAddYes: content['array-cancel-add-yes'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: replaceStrValues(content['array-delete-description'], [
      `${content['claim-information-treatment-noun-plural']}.`,
      `${content['claim-information-treatment-noun-singular']}.`,
    ]),
    deleteNeedAtLeastOneDescription: content['array-empty-hint'],
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(
      content['array-delete-title'],
      `${content['claim-information-treatment-noun-singular']}?`,
    ),
    deleteYes: content['array-delete-yes'],
    alertMaxItems: content['claim-information-treatment-max-alert'],
    getItemName: (item, index) => {
      const facilityName =
        item?.facilityInfo?.vaMedicalCenterName || item?.vaMedicalCenterName;
      return `Treatment ${index + 1}${
        facilityName
          ? `: ${facilityName}`
          : content['claim-information-treatment-item-default-name']
      }`;
    },
    cardDescription: item => {
      const startDate = item?.startDate;
      const endDate = item?.endDate;
      if (startDate && endDate) {
        return `${transformDate(startDate)} - ${transformDate(endDate)}`;
      }
      if (startDate) {
        return `${transformDate(startDate)}`;
      }
      if (endDate) {
        return `${transformDate(endDate)}`;
      }
      return null;
    },
  },
};

/** @returns {PageSchema} */
const introPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['claim-information-treatment-intro-title'],
    }),
    'ui:description': content['claim-information-treatment-intro-description'],
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

/** @returns {PageSchema} */
const summaryPage = {
  uiSchema: {
    'view:isAddingDicBenefits': arrayBuilderYesNoUI(
      options,
      {
        title: content['claim-information-treatment-summary-add'],
        hint: content['array-empty-hint'],
      },
      {
        title: content['claim-information-treatment-summary-add-another'],
        hint: content['array-empty-hint'],
      },
    ),
  },
  schema: {
    type: 'object',
    properties: {
      'view:isAddingDicBenefits': arrayBuilderYesNoSchema,
    },
    required: ['view:isAddingDicBenefits'],
  },
};

/** @returns {PageSchema} */
const nameLocationPageLegacy = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['claim-information-treatment-name-location-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
      lowerCase: false,
    }),
    vaMedicalCenterName: textUI(
      content['claim-information-treatment-name-location-center-name'],
    ),
    city: textUI(content['claim-information-treatment-name-location-city']),
    state: textUI(
      content['claim-information-treatment-name-location-state-region'],
    ),
  },
  schema: {
    type: 'object',
    required: ['state', 'vaMedicalCenterName', 'city'],
    properties: {
      vaMedicalCenterName: textSchema,
      city: textSchema,
      state: textSchema,
    },
  },
};

/** @returns {PageSchema} */
const nameLocationPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['claim-information-treatment-name-location-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
      lowerCase: false,
    }),
    facilityInfo: {
      'ui:field': VaMedicalCenter,
      vaMedicalCenterName: {
        'ui:title':
          content['claim-information-treatment-name-location-center-name'],
        'ui:errorMessages': {
          required:
            content['claim-information-treatment-name-location-error-center'],
        },
      },
      state: {
        'ui:title': content['claim-information-treatment-name-location-state'],
        'ui:errorMessages': {
          required:
            content['claim-information-treatment-name-location-error-state'],
        },
        'ui:confirmationField': ({ formData }) => ({
          label: content['claim-information-treatment-name-location-state'],
          data: STATES_USA.find(s => s.value === formData)?.label,
        }),
      },
      city: {
        'ui:title': content['claim-information-treatment-name-location-city'],
      },
    },
  },
  schema: {
    type: 'object',
    required: ['facilityInfo'],
    properties: {
      facilityInfo: {
        type: 'object',
        required: ['state', 'vaMedicalCenterName', 'city'],
        properties: {
          state: { type: 'string' },
          vaMedicalCenterName: { type: 'string' },
          city: { type: 'string' },
        },
      },
    },
  },
};

/** @returns {PageSchema} */
const treatmentDatePage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['claim-information-treatment-dates-title'],
    ),
    startDate: currentOrPastDateUI({
      title: content['claim-information-treatment-dates-start'],
      monthSelect: false,
    }),
    endDate: currentOrPastDateUI({
      title: content['claim-information-treatment-dates-end'],
      monthSelect: false,
      validations: [validations.isAfterTreatmentStartDate],
    }),
  },
  schema: {
    type: 'object',
    properties: {
      startDate: currentOrPastDateSchema,
      endDate: currentOrPastDateSchema,
    },
    required: [],
  },
};

export const treatmentPages = arrayBuilderPages(options, pageBuilder => ({
  dicBenefitsIntro: pageBuilder.introPage({
    title: content['claim-information-treatment-intro-title'],
    path: 'claim-information/dic/treatment',
    depends: formData =>
      formData?.claims?.dic === true || formData?.claims?.DIC === true,
    uiSchema: introPage.uiSchema,
    schema: introPage.schema,
  }),
  dicBenefitsSummary: pageBuilder.summaryPage({
    title: content['claim-information-treatment-summary-title'],
    path: 'claim-information/dic/add',
    depends: formData =>
      formData?.claims?.dic === true || formData?.claims?.DIC === true,
    uiSchema: summaryPage.uiSchema,
    schema: summaryPage.schema,
  }),
  dicNameLocationPage: pageBuilder.itemPage({
    title: content['claim-information-treatment-name-location-title'],
    path: 'claim-information/dic/:index/name-location',
    depends: formData =>
      (formData?.claims?.dic === true || formData?.claims?.DIC === true) &&
      !formData?.survivorsBenefitsFacilitySelector,
    uiSchema: nameLocationPageLegacy.uiSchema,
    schema: nameLocationPageLegacy.schema,
  }),
  dicNameLocationPageNew: pageBuilder.itemPage({
    title: content['claim-information-treatment-name-location-title'],
    path: 'claim-information/dic/:index/name-location-new',
    depends: formData =>
      (formData?.claims?.dic === true || formData?.claims?.DIC === true) &&
      !!formData?.survivorsBenefitsFacilitySelector,
    uiSchema: nameLocationPage.uiSchema,
    schema: nameLocationPage.schema,
  }),
  dicTreatmentDates: pageBuilder.itemPage({
    title: content['claim-information-treatment-dates-title'],
    path: 'claim-information/dic/:index/dates',
    depends: formData =>
      formData?.claims?.dic === true || formData?.claims?.DIC === true,
    uiSchema: treatmentDatePage.uiSchema,
    schema: treatmentDatePage.schema,
  }),
}));
